/**
 * PcAutoInfoModal — инфо о предстоящем агрегированном PC-автосборе.
 *
 * Показывает:
 *   - общий таймер до следующего автосбора (user-level 24h)
 *   - список всех активных PC-нод с суммой, которая к ним сейчас
 *     накапала, и именем слота
 *
 * Открывается по тапу на ряд «АВТОСБОР PC · N · HH:MM:SS» на дашборде.
 */
import { memo, useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'motion/react';
import { X } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import type { TFunction } from 'i18next';
import { useUISounds } from '@/hooks/useUISounds';
import { useTelegramWebApp } from '@/hooks/useTelegramWebApp';

interface PcContract {
  contractId: string;
  slotName: string;
  pricePaid: number;
  pending: number;
}

interface PcAutoInfoModalProps {
  open: boolean;
  pcNextClaimMs: number | null;
  pcContracts: PcContract[];
  /** Сумма, накопленная с ручных нод (non-PC) на текущий момент.
   *  Отображается отдельной строкой в итоге — чтобы юзер видел
   *  разбивку «авто / ручной». */
  manualPending: number;
  onClose: () => void;
}

function formatHHMMSS(secs: number, t: TFunction): string {
  const total = Math.max(0, Math.floor(secs));
  const h = Math.floor(total / 3600);
  const m = Math.floor((total % 3600) / 60);
  const s = total % 60;
  return t('nodes.timeHMS2', {
    h: h.toString().padStart(2, '0'),
    m: m.toString().padStart(2, '0'),
    s: s.toString().padStart(2, '0'),
  });
}

function PcAutoInfoModalImpl({
  open,
  pcNextClaimMs,
  pcContracts,
  manualPending,
  onClose,
}: PcAutoInfoModalProps) {
  const { t } = useTranslation();
  const uiSounds = useUISounds();
  const { haptic } = useTelegramWebApp();
  // nowMs — снимок времени НА МОМЕНТ ОТКРЫТИЯ модалки. Раньше тут
  // был setInterval(1000) для секундного отсчёта в шапке, но он
  // вызывал перерендер каждую секунду + конфликт с motion/iOS-WebView
  // (модалка визуально «моргала»). Юзер закрывает модалку за пару
  // секунд — таймер всё равно почти статичен, поэтому тик убран.
  const [nowMs, setNowMs] = useState(() => Date.now());
  useEffect(() => {
    if (open) setNowMs(Date.now());
  }, [open]);

  // При открытии — мягкий звук + лёгкий хаптик. Deps СТРОГО [open] —
  // uiSounds/haptic возвращают новые объекты каждый рендер, если их
  // включить в deps — эффект зациклится и звук будет играть раз в кадр.
  useEffect(() => {
    if (!open) return;
    uiSounds.bellOpen();
    haptic.light();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const closeWithFx = () => {
    uiSounds.bellClose();
    haptic.light();
    onClose();
  };

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') closeWithFx();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const secLeft = pcNextClaimMs && pcNextClaimMs > nowMs
    ? Math.max(0, Math.round((pcNextClaimMs - nowMs) / 1000))
    : 0;
  const overdue = !secLeft;

  const totalPending = pcContracts.reduce((s, c) => s + c.pending, 0);

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          className="fixed inset-0 z-50 flex items-center justify-center px-4 py-6"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.22 }}
          onClick={closeWithFx}
        >
          <div className="absolute inset-0 bg-black/65 backdrop-blur-sm" />

          <motion.div
            className="relative w-full max-w-sm rounded-[28px] overflow-hidden flex flex-col"
            style={{
              background:
                'linear-gradient(180deg, var(--glass-fill-strong) 0%, var(--glass-fill) 100%)',
              border: '1px solid var(--glass-stroke-strong)',
              boxShadow:
                'var(--glass-shadow), 0 0 80px -10px color-mix(in oklab, var(--accent-primary) 30%, transparent), inset 0 1.5px 0 0 var(--glass-rim-light), inset 0 -1px 0 0 var(--glass-rim-dark)',
              maxHeight: 'min(560px, calc(100dvh - 80px))',
            }}
            initial={{ y: 20, opacity: 0, scale: 0.95 }}
            animate={{ y: 0, opacity: 1, scale: 1 }}
            exit={{ y: 20, opacity: 0, scale: 0.95 }}
            transition={{ duration: 0.28, ease: [0.32, 0.72, 0, 1] }}
            onClick={(e) => e.stopPropagation()}
          >
            {/* Close X — правый верхний угол */}
            <button
              type="button"
              onClick={closeWithFx}
              aria-label="close"
              className="z-10 w-9 h-9 rounded-full flex items-center justify-center active:opacity-70 transition-opacity"
              style={{
                // position: absolute inline — .lg-pill заставлял relative,
                // из-за чего кнопка становилась обычным flex-child слева.
                position: 'absolute',
                top: 12,
                right: 12,
                background:
                  'linear-gradient(180deg, var(--glass-fill-strong) 0%, var(--glass-fill) 100%)',
                border: '1px solid var(--glass-stroke)',
                boxShadow:
                  'var(--glass-shadow-soft), inset 0 1.5px 0 0 var(--glass-rim-light), inset 0 -1px 0 0 var(--glass-rim-dark)',
              }}
            >
              <X size={16} strokeWidth={2.4} className="text-[var(--text-secondary)]" />
            </button>

            {/* Header — таймер (flex-none). Компактный: лейбл + таймер
                в одну строку, подпись-хинт одной строкой, без лишних
                отступов. */}
            <div className="px-6 pt-5 pb-3 text-center flex-none">
              <p className="text-[10px] uppercase tracking-[0.22em] text-[var(--text-muted)] font-mono">
                {t('pcAutoInfo.title')}
              </p>
              <div className="mt-1.5 font-mono font-extrabold text-[26px] tracking-tight tabular-nums"
                   style={{ color: overdue ? 'var(--accent-primary)' : 'var(--text-primary)' }}>
                {overdue ? t('nodes.pcSoon') : formatHHMMSS(secLeft, t)}
              </div>
              <p className="mt-1.5 text-[10.5px] text-[var(--text-muted)] leading-[1.4] px-2">
                {t('pcAutoInfo.hint', { count: pcContracts.length })}
              </p>
            </div>

            {/* Список нод — забирает всё доступное место, скроллится.
                Компактная высота рядов (py-1.5) чтобы на маленьком
                экране влезало больше без скролла. */}
            <div className="px-4 pb-2 flex-1 overflow-y-auto min-h-0">
              <div className="flex flex-col gap-1">
                {pcContracts.length === 0 && (
                  <p className="text-[11px] text-[var(--text-muted)] text-center py-6">
                    {t('pcAutoInfo.empty')}
                  </p>
                )}
                {pcContracts.map((c) => (
                  <div
                    key={c.contractId}
                    className="lg-pill !rounded-2xl px-3.5 py-1.5 flex items-center justify-between gap-3"
                  >
                    <div className="min-w-0 flex-1">
                      <p className="font-display text-[12.5px] font-bold text-[var(--text-primary)] truncate leading-tight">
                        {c.slotName}
                      </p>
                      <p className="font-mono text-[8.5px] uppercase tracking-[0.14em] text-[var(--text-muted)] leading-tight mt-0.5">
                        {t('pcAutoInfo.nominal', { amount: c.pricePaid.toLocaleString('ru-RU') })}
                      </p>
                    </div>
                    <div className="flex-shrink-0 text-right">
                      <p className="font-mono font-extrabold text-[12.5px] tabular-nums text-[var(--accent-primary)] leading-tight">
                        +{c.pending.toFixed(3)} ₮
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Итог (flex-none): разбивка «авто / ручной» — чтобы
                юзер видел, сколько накапало по PC-автосбору и сколько
                висит в ручном клейме на текущий момент. */}
            {(pcContracts.length > 0 || manualPending > 0) && (
              <div className="px-5 py-2 border-t border-[var(--glass-stroke)] flex flex-col gap-1 flex-none">
                {pcContracts.length > 0 && (
                  <div className="flex items-center justify-between">
                    <span className="font-mono text-[9px] uppercase tracking-[0.18em] text-[var(--text-muted)] font-bold">
                      {t('pcAutoInfo.autoLabel')}
                    </span>
                    <span className="font-display font-extrabold text-[14px] tabular-nums text-[var(--accent-primary)]">
                      +{totalPending.toFixed(3)} USD₮
                    </span>
                  </div>
                )}
                {manualPending > 0 && (
                  <div className="flex items-center justify-between">
                    <span className="font-mono text-[9px] uppercase tracking-[0.18em] text-[var(--text-muted)] font-bold">
                      {t('pcAutoInfo.manualLabel')}
                    </span>
                    <span className="font-display font-extrabold text-[14px] tabular-nums text-[var(--text-secondary)]">
                      +{manualPending.toFixed(3)} USD₮
                    </span>
                  </div>
                )}
              </div>
            )}
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}

export const PcAutoInfoModal = memo(PcAutoInfoModalImpl);
